import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Search, Wrench, AlertTriangle, ChevronRight, Loader2, ImagePlus, Gauge } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fileUrl = (p) => `${API}/files/${p}?auth=${localStorage.getItem("nlc_token")}`;
const CATEGORIES = ["Truck", "Trailer", "Mower", "Trimmer", "Blower", "Edger", "Other"];
const LOCATIONS = ["Main Shop", "Maintenance Area"];

const empty = {
  name: "", category: "Truck", make: "", model: "", year: "", serial: "", photo: "",
  location: "Main Shop", assigned_to: "", track_odometer: false, current_odometer: 0,
  track_engine_hours: false, current_engine_hours: 0, purchase_date: "", notes: "",
};

export default function Equipment() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [photoBusy, setPhotoBusy] = useState(false);

  const load = () => api.get("/equipment").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/team").then((r) => setTeam(r.data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const openNew = () => { setForm(empty); setOpen(true); };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo: data.path }));
    } catch (e) { toast.error("Upload failed"); }
    finally { setPhotoBusy(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      await api.post("/equipment", {
        ...form,
        current_odometer: Number(form.current_odometer) || 0,
        current_engine_hours: Number(form.current_engine_hours) || 0,
      });
      toast.success("Equipment added");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const filtered = items.filter((e) => {
    const hay = [e.name, e.category, e.make, e.model, e.serial, e.location].filter(Boolean).join(" ").toLowerCase();
    const okQ = hay.includes(q.toLowerCase().trim());
    const okS = statusFilter === "all" || (e.status || "in_service") === statusFilter;
    return okQ && okS;
  });

  return (
    <div className="space-y-6" data-testid="equipment-page">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="equipment-search" placeholder="Search equipment…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="equipment-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="in_service">In service</SelectItem>
              <SelectItem value="out_of_service">Out of service</SelectItem>
            </SelectContent>
          </Select>
          {isAdmin && (
            <Button data-testid="add-equipment-btn" onClick={openNew} className="bg-primary text-primary-foreground hover:bg-[#152e17]">
              <Plus className="h-4 w-4 mr-1.5" /> Add Equipment
            </Button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="p-12 text-center border-dashed"><p className="text-muted-foreground">No equipment yet. Add your first asset to start tracking maintenance and work orders.</p></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((e) => {
            const oos = (e.status || "in_service") === "out_of_service";
            return (
              <Card key={e.id} data-testid="equipment-card" onClick={() => navigate(`/equipment/${e.id}`)}
                className="overflow-hidden p-5 border-border rounded-lg hover:-translate-y-1 hover:shadow-lg transition-transform duration-200 bg-card cursor-pointer">
                <div className="-mx-5 -mt-5 mb-4 h-32 overflow-hidden bg-muted">
                  {e.photo ? (
                    <img src={fileUrl(e.photo)} alt={e.name} loading="lazy" className="w-full h-full object-cover" data-testid="equipment-photo" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground"><Wrench className="h-7 w-7 mb-1 opacity-50" /><span className="text-xs">No photo</span></div>
                  )}
                </div>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-display font-bold tracking-tight truncate flex items-center gap-1">{e.name}<ChevronRight className="h-4 w-4 text-muted-foreground" /></h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{[e.category, e.make, e.model].filter(Boolean).join(" · ")}</p>
                  </div>
                  <Badge className={oos ? "bg-destructive/10 text-destructive border-destructive/30" : "bg-primary/10 text-primary border-primary/30"} variant="outline">
                    {oos ? "Out of service" : "In service"}
                  </Badge>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span>📍 {e.location}</span>
                  {e.track_odometer && <span><Gauge className="h-3 w-3 inline mr-0.5" />{Number(e.current_odometer).toLocaleString()} mi</span>}
                  {e.track_engine_hours && <span>⏱ {e.current_engine_hours} hrs</span>}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {e.open_work_orders > 0 && <Badge variant="outline" className="text-[0.65rem] border-amber-300 text-amber-700"><Wrench className="h-3 w-3 mr-1" />{e.open_work_orders} open WO</Badge>}
                  {e.maintenance_due > 0 && <Badge variant="outline" className="text-[0.65rem] border-destructive/40 text-destructive"><AlertTriangle className="h-3 w-3 mr-1" />{e.maintenance_due} maint due</Badge>}
                  {e.open_work_orders === 0 && e.maintenance_due === 0 && <Badge variant="outline" className="text-[0.65rem] border-primary/30 text-primary">All good</Badge>}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto" data-testid="equipment-dialog">
          <DialogHeader><DialogTitle className="font-display">New Equipment</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <label className="block h-32 rounded-lg border-2 border-dashed border-border overflow-hidden cursor-pointer hover:border-primary relative bg-muted">
              {form.photo ? <img src={fileUrl(form.photo)} alt="" className="w-full h-full object-cover" /> : (
                <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
                  {photoBusy ? <Loader2 className="h-6 w-6 animate-spin" /> : <><ImagePlus className="h-6 w-6 mb-1" /><span className="text-xs">Add photo</span></>}
                </div>
              )}
              <input type="file" accept="image/*" className="hidden" data-testid="equipment-photo-input" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1.5"><Label>Name *</Label><Input data-testid="equipment-name-input" value={form.name} onChange={set("name")} placeholder="e.g. Truck 1, ZTR Mower A" /></div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="equipment-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Location</Label>
                <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                  <SelectTrigger data-testid="equipment-location"><SelectValue /></SelectTrigger>
                  <SelectContent>{LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Make</Label><Input value={form.make} onChange={set("make")} /></div>
              <div className="space-y-1.5"><Label>Model</Label><Input value={form.model} onChange={set("model")} /></div>
              <div className="space-y-1.5"><Label>Year</Label><Input value={form.year} onChange={set("year")} /></div>
              <div className="space-y-1.5"><Label>Serial / VIN</Label><Input value={form.serial} onChange={set("serial")} /></div>
              <div className="col-span-2 space-y-1.5">
                <Label>Assigned to</Label>
                <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="equipment-assigned"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Unassigned</SelectItem>{team.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-lg border border-border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="cursor-pointer">Track odometer (miles)</Label>
                <Switch checked={form.track_odometer} onCheckedChange={(v) => setForm({ ...form, track_odometer: v })} data-testid="track-odometer-switch" />
              </div>
              {form.track_odometer && <Input type="number" value={form.current_odometer} onChange={set("current_odometer")} placeholder="Current odometer" data-testid="odometer-input" />}
              <div className="flex items-center justify-between">
                <Label className="cursor-pointer">Track engine hours</Label>
                <Switch checked={form.track_engine_hours} onCheckedChange={(v) => setForm({ ...form, track_engine_hours: v })} data-testid="track-hours-switch" />
              </div>
              {form.track_engine_hours && <Input type="number" value={form.current_engine_hours} onChange={set("current_engine_hours")} placeholder="Current engine hours" data-testid="hours-input" />}
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-equipment-btn" onClick={save} className="bg-primary text-primary-foreground hover:bg-[#152e17]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
